import { ImagineChat } from "./imagine-chat";
import {
  ChatOnDataCallback,
  ChatOnErrorCallback,
  ChatOnFinishCallback,
  DefaultChatTransport,
} from "ai";
import { imagineConfigStore } from "@/stores/imagine-config.store";
import { getConsoleAuthToken } from "../use-console-auth-token";
import type { ImagineUIMessage } from "@imagine/ai-service/shared-types";

export function createImagineResumableChat({
  projectId,
  onData,
  onFinish,
  onError,
  onMessagesChange,
  onStatusChange,
  onAssistantMessageChange,
}: {
  projectId: string;
  onData?: ChatOnDataCallback<ImagineUIMessage>;
  onFinish?: ChatOnFinishCallback<ImagineUIMessage>;
  onError?: ChatOnErrorCallback;
  onMessagesChange?: (messages: ImagineUIMessage[]) => void;
  onStatusChange?: (status: string) => void;
  onAssistantMessageChange?: (message: ImagineUIMessage) => void;
}) {
  const endpoint = imagineConfigStore.getState().config?.AI_SERVICE_ENDPOINT;

  if (!endpoint) {
    throw new Error("AI service endpoint is not configured");
  }

  const chat = new ImagineChat<ImagineUIMessage>({
    id: projectId,
    onData,
    onFinish,
    onError,
    transport: new DefaultChatTransport({
      prepareReconnectToStreamRequest: async () => {
        return {
          api: `${endpoint}/api/chat/${projectId}/stream`,
          headers: {
            "X-Imagine-Token": await getConsoleAuthToken(),
            "X-Imagine-Session-Id":
              imagineConfigStore.getState().consoleSessionId,
          },
        };
      },
    }),
  });

  if (onMessagesChange) {
    chat.registerMessagesCallback(() => {
      onMessagesChange(chat.messages);
    });
  }

  if (onAssistantMessageChange) {
    chat.registerMessagesCallback(() => {
      const latestMessage = chat.messages[chat.messages.length - 1];
      if (latestMessage?.role === "assistant") {
        onAssistantMessageChange(latestMessage);
      }
    });
  }

  if (onStatusChange) {
    chat.registerStatusCallback(() => {
      onStatusChange(chat.status);
    });
  }

  return chat;
}
